/**
 * Lo que muestra «Tu día», la pantalla de inicio (Req. 24; diseño Foco).
 *
 * Código puro: recibe los registros del día (comidas y entrenamientos) y
 * arma lo que se presenta. No inventa datos: sin registros, lo dice. Los
 * conteos ("2 de 3 comidas") salen siempre de los registros reales y de la
 * fecha local.
 */

// Solo tipos: el dominio no depende de Dexie en tiempo de ejecución.
import type { MealEntry, Modalidad, TiempoComida, Workout } from '@/db/types';
import { diaDelMes, nombreDia, nombreMes, type FechaLocal } from './dates';
import { formatearHoraDeInstante, formatearDuracion, minutosEntre } from './time';
import { formatearNumero } from './numbers';
import { modalidadDe } from './modalidad';
import type { Propuestas, SiguienteDelCiclo } from './sesiones';

// ---------------------------------------------------------------------------
// Fechas presentables
// ---------------------------------------------------------------------------

function mayuscula(texto: string): string {
  return texto.charAt(0).toLocaleUpperCase('es-MX') + texto.slice(1);
}

/** "Jueves, 22 de octubre" */
export function fechaDelDia(fecha: FechaLocal): string {
  return `${mayuscula(nombreDia(fecha))}, ${diaDelMes(fecha)} de ${nombreMes(fecha)}`;
}

/** "Vie, 23 oct" */
export function fechaCorta(fecha: FechaLocal): string {
  return `${mayuscula(nombreDia(fecha).slice(0, 3))}, ${diaDelMes(fecha)} ${nombreMes(fecha).slice(0, 3)}`;
}

// ---------------------------------------------------------------------------
// Resumen de comidas
// ---------------------------------------------------------------------------

/** Los tres tiempos del resumen. Las colaciones se cuentan aparte. */
export const TIEMPOS_DEL_RESUMEN = ['desayuno', 'comida', 'cena'] as const;
export type TiempoDelResumen = (typeof TIEMPOS_DEL_RESUMEN)[number];

export const ETIQUETAS_TIEMPO: Readonly<Record<TiempoComida, string>> = {
  desayuno: 'Desayuno',
  comida: 'Comida',
  cena: 'Cena',
  colacion: 'Colación',
};

export interface ResumenTiempo {
  tiempo: TiempoDelResumen;
  etiqueta: string;
  /** Cuántas entradas tiene; puede haber varias en una misma comida. */
  registros: number;
  /** "Tortilla, Café negro y 2 más · 320 kcal", o null si no hay registros. */
  descripcion: string | null;
}

export interface ResumenComidas {
  tiempos: ResumenTiempo[];
  /** Cuántos de los tres tiempos tienen al menos un registro. */
  registrados: number;
  total: number;
  colaciones: { registros: number; kcal: number };
}

/** Nombres distintos que se muestran antes de "y N más". */
const NOMBRES_VISIBLES = 2;

function kcalDe(entradas: readonly MealEntry[]): number {
  return entradas.reduce((suma, e) => suma + (Number.isFinite(e.valores?.kcal) ? e.valores.kcal : 0), 0);
}

function formatearKcal(kcal: number): string {
  return `${formatearNumero(kcal, { decimales: 0 })} kcal`;
}

function describir(entradas: readonly MealEntry[]): string | null {
  if (entradas.length === 0) return null;
  const nombres: string[] = [];
  for (const entrada of entradas) {
    const nombre = entrada.nombre.trim();
    if (nombre !== '' && !nombres.includes(nombre)) nombres.push(nombre);
  }
  const visibles = nombres.slice(0, NOMBRES_VISIBLES);
  const restantes = nombres.length - visibles.length;
  const lista =
    visibles.length === 0
      ? `${entradas.length} ${entradas.length === 1 ? 'registro' : 'registros'}`
      : restantes > 0
        ? `${visibles.join(', ')} y ${restantes} más`
        : visibles.join(visibles.length === 2 ? ' y ' : ', ');
  return `${lista} · ${formatearKcal(kcalDe(entradas))}`;
}

/** Resume las comidas de UN día. Las entradas pueden venir en cualquier orden. */
export function resumirComidas(entradas: readonly MealEntry[]): ResumenComidas {
  const ordenadas = [...entradas].sort((a, b) => a.momento.localeCompare(b.momento));
  const tiempos = TIEMPOS_DEL_RESUMEN.map((tiempo): ResumenTiempo => {
    const delTiempo = ordenadas.filter((e) => e.tiempo === tiempo);
    return {
      tiempo,
      etiqueta: ETIQUETAS_TIEMPO[tiempo],
      registros: delTiempo.length,
      descripcion: describir(delTiempo),
    };
  });
  const colaciones = ordenadas.filter((e) => e.tiempo === 'colacion');
  return {
    tiempos,
    registrados: tiempos.filter((t) => t.registros > 0).length,
    total: TIEMPOS_DEL_RESUMEN.length,
    colaciones: { registros: colaciones.length, kcal: kcalDe(colaciones) },
  };
}

/** "1 colación · 120 kcal", "2 colaciones · 250 kcal" o null. */
export function textoColaciones(colaciones: ResumenComidas['colaciones']): string | null {
  if (colaciones.registros === 0) return null;
  const nombre = colaciones.registros === 1 ? 'colación' : 'colaciones';
  return `${colaciones.registros} ${nombre} · ${formatearKcal(colaciones.kcal)}`;
}

// ---------------------------------------------------------------------------
// Sesión de entrenamiento y actividad del día
// ---------------------------------------------------------------------------

export interface EntrenamientoConEjercicios {
  entrenamiento: Workout;
  ejercicios: number;
}

/**
 * La próxima sesión que propone Gym (tarea 14): la que toca hoy, la que
 * faltó o, en las sesiones sin días fijos, la que sigue. Solo se propone.
 */
export interface ProximaSesion {
  motivo: 'hoy' | 'pendiente' | 'sigue';
  routineId: string;
  nombre: string;
  /** El día al que corresponde: hoy, o el día que faltó; null en las que siguen del ciclo. */
  fecha: FechaLocal | null;
  modalidad: Modalidad;
  /** Con la de hoy: la que también faltó, para decirlo. */
  tambien: { nombre: string; fecha: FechaLocal } | null;
  /** En las sesiones sin días fijos: la última del ciclo. */
  ultimaDelCiclo: { nombre: string; fecha: FechaLocal } | null;
}

/**
 * Lo mismo que propone Gym en la modalidad que muestra, en este orden: la de
 * hoy si no está hecha (con la que faltó, si hay), la que faltó y la que
 * sigue del ciclo. Nada se da por hecho ni empieza solo (decisión de Janeth,
 * 23 oct 2026).
 */
export function proximaSesion(
  propuestas: Propuestas,
  siguientes: readonly SiguienteDelCiclo[],
  modalidad: Modalidad,
): ProximaSesion | null {
  const { hoy, pendiente } = propuestas;
  const base = { modalidad, tambien: null, ultimaDelCiclo: null };
  if (hoy !== null && !hoy.hecha) {
    return {
      ...base,
      motivo: 'hoy',
      routineId: hoy.routineId,
      nombre: hoy.nombre,
      fecha: hoy.fecha,
      tambien: pendiente === null ? null : { nombre: pendiente.nombre, fecha: pendiente.fecha },
    };
  }
  if (pendiente !== null) {
    return { ...base, motivo: 'pendiente', routineId: pendiente.routineId, nombre: pendiente.nombre, fecha: pendiente.fecha };
  }
  const sigue = siguientes[0];
  if (sigue !== undefined && hoy === null) {
    return { ...base, motivo: 'sigue', routineId: sigue.routineId, nombre: sigue.nombre, fecha: null, ultimaDelCiclo: sigue.ultima };
  }
  return null;
}

/**
 * Cuentan los entrenamientos de las dos modalidades: la sesión dice si fue en
 * el gimnasio o en casa (0.3.4).
 */
export type SesionDelDia =
  /** Hay un entrenamiento abierto (Req. 24.1: "el entrenamiento en curso"). */
  | { tipo: 'en_curso'; nombre: string; desde: string; ejercicios: number; modalidad: Modalidad }
  /** Se terminó un entrenamiento hoy. */
  | { tipo: 'hoy'; nombre: string; duracion: string | null; ejercicios: number; modalidad: Modalidad }
  /** La que propone Gym, con la última sesión para dar contexto (tarea 14). */
  | { tipo: 'proxima'; proxima: ProximaSesion; ultima: { nombre: string; fecha: FechaLocal } | null }
  /** El último entrenamiento, de otro día (Req. 24.1: "o el último"). */
  | { tipo: 'ultima'; nombre: string; fecha: FechaLocal; duracion: string | null; ejercicios: number; modalidad: Modalidad }
  | { tipo: 'ninguna' };

function duracionDe(w: Workout): string | null {
  if (w.fin === null) return null;
  try {
    const minutos = minutosEntre(w.inicio, w.fin);
    return minutos > 0 ? formatearDuracion(minutos) : null;
  } catch {
    return null;
  }
}

function horaDe(iso: string): string {
  try {
    return formatearHoraDeInstante(iso);
  } catch {
    return '';
  }
}

/**
 * La sesión que se muestra en «Tu día»: la que está en curso, la de hoy si ya
 * se entrenó, la próxima que propone Gym (tarea 14) o la última. La próxima
 * sale de las mismas reglas de Gym (`proximaSesion`): no se adivina nada.
 */
export function sesionDelDia(
  hoy: FechaLocal,
  enCurso: EntrenamientoConEjercicios | null,
  ultima: EntrenamientoConEjercicios | null,
  proxima: ProximaSesion | null = null,
): SesionDelDia {
  if (enCurso !== null) {
    return {
      tipo: 'en_curso',
      nombre: enCurso.entrenamiento.nombre,
      desde: horaDe(enCurso.entrenamiento.inicio),
      ejercicios: enCurso.ejercicios,
      modalidad: modalidadDe(enCurso.entrenamiento),
    };
  }
  if (ultima !== null && ultima.entrenamiento.fecha === hoy) {
    const { entrenamiento, ejercicios } = ultima;
    return {
      tipo: 'hoy',
      nombre: entrenamiento.nombre,
      duracion: duracionDe(entrenamiento),
      ejercicios,
      modalidad: modalidadDe(entrenamiento),
    };
  }
  if (proxima !== null) {
    return {
      tipo: 'proxima',
      proxima,
      ultima: ultima === null ? null : { nombre: ultima.entrenamiento.nombre, fecha: ultima.entrenamiento.fecha },
    };
  }
  if (ultima === null) return { tipo: 'ninguna' };
  const { entrenamiento, ejercicios } = ultima;
  const modalidad = modalidadDe(entrenamiento);
  return {
    tipo: 'ultima',
    nombre: entrenamiento.nombre,
    fecha: entrenamiento.fecha,
    duracion: duracionDe(entrenamiento),
    ejercicios,
    modalidad,
  };
}

const enCasa = (s: { modalidad: Modalidad }) => (s.modalidad === 'casa' ? ' en casa' : '');

export type ActividadDelDia =
  | { tipo: 'en_curso'; texto: string }
  | { tipo: 'entreno'; texto: string }
  | { tipo: 'sin_registro'; texto: string }
  | { tipo: 'pendiente'; texto: string };

/**
 * La línea "Actividad de hoy". Sin un calendario de entrenamientos no se puede
 * saber si hoy tocaba descansar, así que no se afirma "descanso": se dice lo
 * que hay registrado. Si el registro de entrenamientos todavía no existe,
 * se dice en qué fase llega.
 */
export function actividadDelDia(
  sesion: SesionDelDia,
  gymDisponible: boolean,
  /** Fase en que llega el registro de entrenamientos (areas.ts). */
  faseGym = 2,
): ActividadDelDia {
  if (sesion.tipo === 'en_curso') {
    return { tipo: 'en_curso', texto: `entrenamiento en curso${enCasa(sesion)} · ${sesion.nombre}` };
  }
  if (sesion.tipo === 'hoy') {
    return {
      tipo: 'entreno',
      texto: `entrenaste${enCasa(sesion)} · ${sesion.nombre}${sesion.duracion === null ? '' : ` · ${sesion.duracion}`}`,
    };
  }
  if (!gymDisponible) {
    return { tipo: 'pendiente', texto: `el registro de entrenamientos llega en la Fase ${faseGym}` };
  }
  return { tipo: 'sin_registro', texto: 'sin entrenamiento registrado' };
}

/** "1 ejercicio", "5 ejercicios". */
export function textoEjercicios(n: number): string {
  return n === 1 ? '1 ejercicio' : `${n} ejercicios`;
}
